/**
 * CampaignAdjustPanel — defensive ADJUSTMENT review for a staged campaign (all tranches as
 * one position). Shows the aggregate status (loss vs ₹ SL, Greeks), scenario-aware candidate
 * adjustments (harvest premium on the untested side / protective hedge), lets the user tweak
 * the new legs' strikes, previews the resulting payoff, and places it (add-only). The trade
 * is NOT closed here — it stays open until the ₹ SL amount is hit.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Button, Chip, Stack, Alert, CircularProgress, IconButton, Divider, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import toast from 'react-hot-toast';
import { optionsService } from '../../services/options.service';
import PayoffChartPro from '../charts/PayoffChartPro';
import { thetaVerdict, deltaVerdict, vegaVerdict } from './campaignGreeks';

/** Parse an option symbol (C-BTC-62000-060725) → readable "SELL PE 62000". */
const parseSym = (sym: string, action?: string) => {
  const p = String(sym).split('-'); const type = p[0] === 'C' ? 'CE' : 'PE'; const strike = Number(p[2]) || 0;
  return `${action || ''} ${type} ${Math.round(strike).toLocaleString('en-IN')}`.trim();
};

const fmtInr = (v: number) => `₹${Math.round(v).toLocaleString('en-IN')}`;
const fmtSigned = (v: number) => `${v >= 0 ? '+' : '−'}₹${Math.round(Math.abs(v)).toLocaleString('en-IN')}`;
const fmtPx = (v: number) => Math.round(Number(v)).toLocaleString('en-IN');

const CampaignAdjustPanel: React.FC<{ campaignId: number; symbol?: string; tradingMode?: string; onAdjusted?: () => void }> = ({ campaignId, symbol, tradingMode, onAdjusted }) => {
  const [adj, setAdj] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [selId, setSelId] = useState<string | null>(null);
  const [editLegs, setEditLegs] = useState<any[] | null>(null);
  const [projected, setProjected] = useState<any>(null);
  const [previewing, setPreviewing] = useState(false);
  const [placing, setPlacing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r: any = await optionsService.getCampaignAdjustment(campaignId);
      const d = r?.data?.data; setAdj(d);
      const first = d?.candidates?.[0];
      setSelId(first?.id || null); setEditLegs(first ? first.openLegs.map((o: any) => ({ ...o })) : null); setProjected(first?.projected || null);
    } catch (e: any) { setErr(e?.response?.data?.error?.message || 'Could not analyse the campaign'); }
    finally { setLoading(false); }
  }, [campaignId]);
  useEffect(() => { load(); }, [load]);

  const base = useMemo(() => String(symbol || '').replace('USD', '') || String(adj?.legs?.[0]?.symbol || '').split('-')[1] || '', [symbol, adj]);
  const chain = adj?.chain;
  const candidates: any[] = adj?.candidates || [];
  const selected = candidates.find(c => c.id === selId) || null;

  const selectCand = (c: any) => { setSelId(c.id); setEditLegs(c.openLegs.map((o: any) => ({ ...o }))); setProjected(c.projected || null); };

  // Re-price the FULL resulting position when the user steps a strike.
  const reprice = useCallback(async (legs: any[]) => {
    if (!adj?.legs?.length) return;
    setPreviewing(true);
    try {
      const survivors = adj.legs.map((l: any) => ({ action: l.action, optionType: l.optionType, strike: l.strike, expiry: l.expiry }));
      const full = [...survivors, ...legs.map((o: any) => ({ action: o.action, optionType: o.optionType, strike: o.strike, expiry: o.expiry }))];
      const r: any = await optionsService.previewBasket({ underlyingSymbol: symbol || `${base}USD`, strategyId: 'custom', legs: full });
      if (r?.data?.data?.card) setProjected(r.data.data.card);
    } catch { /* keep prior */ }
    finally { setPreviewing(false); }
  }, [adj, symbol, base]);

  const stepStrike = (i: number, dir: 1 | -1) => {
    if (!editLegs) return;
    const step = Number(chain?.strikeStep) || 1;
    const next = editLegs.map((o, j) => j === i ? { ...o, strike: o.strike + dir * step } : o);
    setEditLegs(next); reprice(next);
  };

  const isCloseOnly = !!selected?.closeOnly || (!!selected?.closeLegs?.length && !(editLegs?.length));
  const canPlace = (editLegs?.length || 0) > 0 || !!selected?.closeLegs?.length;

  const place = async () => {
    if (!canPlace) return;
    setPlacing(true);
    try {
      const body: any = { openLegs: editLegs || [] };
      if (selected?.closeLegs?.length) { body.closeLegs = selected.closeLegs; if (selected.basketId) body.targetBasketId = selected.basketId; }
      await optionsService.adjustCampaign(campaignId, body);
      toast.success(selected?.healthy ? 'Tranche rolled.' : selected?.cut ? 'Bleeding leg squared off.' : 'Adjustment placed on the campaign.', { duration: 7000 }); onAdjusted?.(); load();
    }
    catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Adjustment failed', { duration: 8000 }); }
    finally { setPlacing(false); }
  };

  if (loading && !adj) return <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}><CircularProgress size={16} /> <Typography variant="caption">Analysing the campaign…</Typography></Box>;
  if (err) return <Alert severity="warning" sx={{ my: 1 }}>{err}</Alert>;
  if (!adj) return null;

  const ag = adj.aggregate || {};
  const v = adj.verdict || {};

  return (
    <Box>
      {/* Aggregate status — Greeks colour-coded FOR (green) / AGAINST (red) the view. */}
      {(() => { const view = String(ag.view || ''); const tv = thetaVerdict(ag.thetaInrPerDay || 0), dv = deltaVerdict(view, ag.netDelta || 0), vv = vegaVerdict(view, ag.netVega || 0); return (
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', p: 1, mb: 1, borderRadius: 1.5, bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,.04)' : '#f6f7f9', alignItems: 'center' }}>
        <M label="Loss so far" value={fmtInr(ag.lossInr || 0)} color="#c62828" />
        <Divider orientation="vertical" flexItem />
        <M label="🛑 SL (exit at)" value={ag.slAmountInr != null ? fmtInr(ag.slAmountInr) : '—'} color="#c62828" />
        {ag.tpAmountInr != null && <><Divider orientation="vertical" flexItem /><M label="🎯 TP (book at)" value={fmtInr(ag.tpAmountInr)} color="#2e7d32" /></>}
        <Divider orientation="vertical" flexItem />
        <M label="Θ / day" value={fmtSigned(ag.thetaInrPerDay || 0)} color={tv.color} hint={tv.note} />
        <Divider orientation="vertical" flexItem />
        <M label="Net Δ" value={(ag.netDelta || 0).toFixed(2)} color={dv.color} hint={dv.note} />
        <Divider orientation="vertical" flexItem />
        <M label="Vega" value={fmtSigned(ag.netVega || 0)} color={vv.color} hint={vv.note} />
        <Divider orientation="vertical" flexItem />
        <M label="ROI" value={`${ag.roiPct || 0}%`} />
      </Box>
      ); })()}

      {/* CURRENT position payoff — always shown (the deployed tranches' combined payoff). */}
      {adj.currentPayoff?.payoffData?.spotRange?.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', mb: 0.3 }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>Current position payoff</Typography>
            <Typography variant="caption"><b>Max profit:</b> <span style={{ color: '#2e7d32' }}>{fmtInr(Math.abs(adj.currentPayoff.maxProfit || 0))}</span></Typography>
            <Typography variant="caption"><b>Max loss:</b> <span style={{ color: '#c62828' }}>{adj.currentPayoff.maxLoss === 0 ? '∞' : fmtInr(Math.abs(adj.currentPayoff.maxLoss || 0))}</span></Typography>
          </Stack>
          <PayoffChartPro basket={{ payoffData: adj.currentPayoff.payoffData, maxProfit: adj.currentPayoff.maxProfit, maxLoss: adj.currentPayoff.maxLoss, breakEvens: adj.currentPayoff.breakEvens, legs: [] }} spotPrice={Number(ag.spot) || 0} underlyingSymbol={base} hideHeader />
        </Box>
      )}

      {v.reasons?.length > 0 && (
        <Alert severity={v.severity === 'urgent' ? 'error' : v.severity === 'warning' ? 'warning' : 'info'} sx={{ py: 0.3, mb: 1, fontSize: '0.78rem' }}>
          {v.reasons.join(' ')} {v.testedSide ? `(tested side: ${v.testedSide === 'PE' ? 'downside' : 'upside'})` : ''}
        </Alert>
      )}

      {!candidates.length ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No adjustment suggestions right now — the position is within tolerance. It stays open until the ₹ SL amount is hit.</Typography>
      ) : <>
        {/* Candidate chooser */}
        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Suggested adjustments</Typography>
        <Stack spacing={0.75} sx={{ mb: 1 }}>
          {candidates.map((c: any) => {
            const on = c.id === selId;
            return (
              <Box key={c.id} onClick={() => selectCand(c)} sx={{ cursor: 'pointer', p: 1, borderRadius: 1.5, border: t => `1.5px solid ${on ? t.palette.primary.main : t.palette.divider}` }}>
                <Typography sx={{ fontWeight: 800, fontSize: '0.82rem' }}>{c.label}</Typography>
                <Typography variant="caption" color="text.secondary">{c.rationale}</Typography>
              </Box>
            );
          })}
        </Stack>

        {selected && <>
          {/* Legs being SQUARED OFF (bleeding-leg cut / roll). */}
          {selected.closeLegs?.length > 0 && <>
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: '#c62828' }}>Squaring off (bleeding / spent leg)</Typography>
            <Stack direction="row" spacing={0.6} sx={{ flexWrap: 'wrap', rowGap: 0.6, mb: 0.75, mt: 0.3 }}>
              {selected.closeLegs.map((cl: any, i: number) => (
                <Chip key={i} size="small" label={parseSym(cl.symbol, cl.action === 'SELL' ? 'buy-to-close' : 'sell-to-close')} sx={{ height: 20, fontSize: '0.6rem', bgcolor: '#fff3e0', color: '#e65100', fontWeight: 700 }} />
              ))}
            </Stack>
          </>}

          {editLegs && editLegs.length > 0 && <>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>{selected.cut ? 'Re-establish (new legs — tweak strikes if you like)' : 'New legs (tweak strikes if you like)'} {previewing && <CircularProgress size={11} sx={{ ml: 1 }} />}</Typography>
          <Table size="small" sx={{ mb: 0.75, '& td, & th': { py: 0.25, px: 1 } }}>
            <TableHead><TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Side</TableCell><TableCell sx={{ fontWeight: 700 }}>Opt</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Strike</TableCell><TableCell sx={{ fontWeight: 700 }} align="right">Qty</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Expiry</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {editLegs.map((l: any, i: number) => (
                <TableRow key={i}>
                  <TableCell><Chip size="small" label={l.action} sx={{ height: 18, fontSize: '0.56rem', fontWeight: 700, bgcolor: l.action === 'BUY' ? '#e8f5e9' : '#ffebee', color: l.action === 'BUY' ? '#1b5e20' : '#b71c1c' }} /></TableCell>
                  <TableCell>{l.optionType}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.3} alignItems="center" justifyContent="center">
                      <IconButton size="small" sx={{ p: 0.2 }} onClick={() => stepStrike(i, -1)}><RemoveIcon sx={{ fontSize: 13 }} /></IconButton>
                      <Typography sx={{ minWidth: 52, textAlign: 'center', fontWeight: 700, fontSize: '0.78rem' }}>{fmtPx(l.strike)}</Typography>
                      <IconButton size="small" sx={{ p: 0.2 }} onClick={() => stepStrike(i, +1)}><AddIcon sx={{ fontSize: 13 }} /></IconButton>
                    </Stack>
                  </TableCell>
                  <TableCell align="right">{l.quantity}</TableCell>
                  <TableCell>{String(l.expiry).slice(5, 10)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </>}

          {projected && (
            <Box sx={{ mb: 1 }}>
              <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', mb: 0.3 }}>
                <Typography variant="caption"><b>After adjustment — Max profit:</b> <span style={{ color: '#2e7d32' }}>{fmtInr(Math.abs(projected.maxProfit || 0))}</span></Typography>
                <Typography variant="caption"><b>Max loss:</b> <span style={{ color: '#c62828' }}>{projected.maxLoss === 0 ? '∞' : fmtInr(Math.abs(projected.maxLoss || 0))}</span></Typography>
                <Typography variant="caption"><b>Net {Number(projected.netPremium) < 0 ? 'credit' : 'debit'}:</b> {fmtInr(Math.abs(Number(projected.netPremium) || 0))}</Typography>
              </Stack>
              <PayoffChartPro basket={projected} spotPrice={Number(ag.spot) || 0} underlyingSymbol={base} hideHeader />
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
              {isCloseOnly ? `Squares off the bleeding/spent leg on tranche #${selected.basketId} (${tradingMode || 'PAPER'}). The rest of the campaign stays open until the ₹ SL amount is hit.`
                : selected.cut ? `Squares off the bleeding leg AND opens the re-established legs (${tradingMode || 'PAPER'}). The campaign stays open until the ₹ SL amount is hit.`
                : `Places the new legs as a ${tradingMode || 'PAPER'} tranche of this campaign. The trade stays open until the ₹ SL amount is hit.`}
            </Typography>
            <Button size="small" variant="contained" color={tradingMode === 'LIVE' ? 'error' : 'primary'} disabled={placing || !canPlace} onClick={place}
              startIcon={placing ? <CircularProgress size={14} color="inherit" /> : undefined}>
              {placing ? 'Placing…' : isCloseOnly ? `Square off (${tradingMode || 'PAPER'})` : `Place adjustment (${tradingMode || 'PAPER'})`}
            </Button>
          </Box>
        </>}
      </>}
    </Box>
  );
};

const M: React.FC<{ label: string; value: React.ReactNode; color?: string; hint?: string }> = ({ label, value, color, hint }) => (
  <Tooltip arrow title={hint || ''} disableHoverListener={!hint}>
    <Box sx={{ px: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</Typography>
      <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color }}>{value}</Typography>
    </Box>
  </Tooltip>
);

export default CampaignAdjustPanel;
